import { EventTypes } from '../../render/constants';
import { Rect, Region } from '../region';

export function eventToScreenPoint(renderContext, e) {
  const canvas = renderContext.context.canvas;
  const dpr = renderContext.dpr;
  const canvasRect = canvas.getBoundingClientRect();
  const canvasX = canvasRect.x;
  const canvasY = canvasRect.y;
  return {
    x: (e.clientX - canvasX) * dpr,
    y: (e.clientY - canvasY) * dpr,
  };
}

export function getScrollbarHandlers(renderContext) {
  const handlers = [];
  const info = getScrollbarInfo(renderContext);
  if (info.h) {
    const handler = info.h.thumb;
    handler.id = 'scrollbar-h';
    handler.type = 'scrollbar';
    handler.trackWidth = info.h.trackWidth;
    handler.thumbWidth = info.h.thumbWidth;
    handler.totalBox = info.h.totalBox;
    handler.viewRect = info.h.viewRect;
    handlers.push(handler);
  }
  if (info.v) {
    const handler = info.v.thumb;
    handler.id = 'scrollbar-v';
    handler.type = 'scrollbar';
    handler.trackHeight = info.v.trackHeight;
    handler.thumbHeight = info.v.thumbHeight;
    handler.totalBox = info.v.totalBox;
    handler.viewRect = info.v.viewRect;
    handlers.push(handler);
  }
  return handlers;
}

const SCROLLBAR_MARGIN = 4;
const SCROLLBAR_SIZE = 12; // 固定 scrollbar 尺寸，不跟随 scale 变化

export function getScrollbarInfo(renderContext) {
  const { context } = renderContext;
  const { scale: scaleProvider } = renderContext.providers;
  const canvas = context.canvas;
  const canvasWidth = canvas.width;
  const canvasHeight = canvas.height;

  // Viewport dimensions in world coordinates
  const viewWidth = canvasWidth / scaleProvider.scale;
  const viewHeight = canvasHeight / scaleProvider.scale;

  const rootNodes = renderContext.api.getChildren(null);
  const contentBox = renderContext.api.getNodesRegion(rootNodes);

  // Calculate scrollable area - always create a larger area for navigation
  let totalBox;
  const viewRect = new Rect(
    -scaleProvider.translateX / scaleProvider.scale,
    -scaleProvider.translateY / scaleProvider.scale,
    viewWidth,
    viewHeight,
  );

  if (!contentBox || (contentBox.w === 0 && contentBox.h === 0)) {
    // No content - create a default scrollable area (6x viewport for better navigation)
    totalBox = new Rect(
      viewRect.x - viewWidth * 2.5,
      viewRect.y - viewHeight * 2.5,
      viewWidth * 6,
      viewHeight * 6,
    );
  } else {
    // Content exists - expand content box to create reasonable boundaries
    const contentRegion = Region.fromRect(contentBox);
    const viewRegion = Region.fromRect(viewRect);
    const unionRegion = contentRegion.union(viewRegion);

    // Always expand to provide navigation space, minimum 3x viewport
    const expandedWidth = Math.max(unionRegion.rect.w * 1.5, viewWidth * 3);
    const expandedHeight = Math.max(unionRegion.rect.h * 1.5, viewHeight * 3);

    totalBox = new Rect(
      unionRegion.rect.x - (expandedWidth - unionRegion.rect.w) / 2,
      unionRegion.rect.y - (expandedHeight - unionRegion.rect.h) / 2,
      expandedWidth,
      expandedHeight,
    );
  }

  // Always show scrollbars for consistent navigation experience
  const showH = true;
  const showV = true;

  const paddingRight = (renderContext.paddingRight || 0) * renderContext.dpr;
  const hTrackWidth =
    canvasWidth - (showV ? SCROLLBAR_SIZE + SCROLLBAR_MARGIN * 2 : SCROLLBAR_MARGIN) - paddingRight;
  const vTrackHeight =
    canvasHeight - (showH ? SCROLLBAR_SIZE + SCROLLBAR_MARGIN * 2 : SCROLLBAR_MARGIN);

  const info = { showH, showV, h: null, v: null };

  if (showH) {
    // thumb 尺寸
    const minThumbWidth = 20;
    const idealThumbWidth = hTrackWidth * (viewWidth / totalBox.w);
    const thumbWidth = Math.max(minThumbWidth, idealThumbWidth);

    // 安全边界内的可用滚动距离
    const availableTrackDistance = hTrackWidth - thumbWidth;

    // thumb 位置基于安全边界
    let thumbX = 0;
    if (availableTrackDistance > 0) {
      // 计算视图在总区域中的相对位置
      const viewOffsetInTotal = viewRect.x - totalBox.x;
      const scrollableWidth = totalBox.w - viewWidth;

      if (scrollableWidth > 0) {
        const progress = Math.max(0, Math.min(1, viewOffsetInTotal / scrollableWidth));
        thumbX = progress * availableTrackDistance;
      }
    }

    info.h = {
      track: new Rect(
        SCROLLBAR_MARGIN,
        canvasHeight - SCROLLBAR_SIZE - SCROLLBAR_MARGIN,
        hTrackWidth,
        SCROLLBAR_SIZE,
      ),
      thumb: new Rect(
        SCROLLBAR_MARGIN + thumbX,
        canvasHeight - SCROLLBAR_SIZE - SCROLLBAR_MARGIN,
        thumbWidth,
        SCROLLBAR_SIZE,
      ),
      trackWidth: hTrackWidth,
      thumbWidth: thumbWidth,
      totalBox: totalBox,
      viewRect: viewRect,
    };
  }

  if (showV) {
    // thumb 尺寸
    const minThumbHeight = 20;
    const idealThumbHeight = vTrackHeight * (viewHeight / totalBox.h);
    const thumbHeight = Math.max(minThumbHeight, idealThumbHeight);

    // 安全边界内的可用滚动距离
    const availableTrackDistance = vTrackHeight - thumbHeight;

    // thumb 位置基于安全边界
    let thumbY = 0;
    if (availableTrackDistance > 0) {
      // 计算视图在总区域中的相对位置
      const viewOffsetInTotal = viewRect.y - totalBox.y;
      const scrollableHeight = totalBox.h - viewHeight;

      if (scrollableHeight > 0) {
        const progress = Math.max(0, Math.min(1, viewOffsetInTotal / scrollableHeight));
        thumbY = progress * availableTrackDistance;
      }
    }

    const paddingRight = (renderContext.paddingRight || 0) * renderContext.dpr;

    info.v = {
      track: new Rect(
        canvasWidth - SCROLLBAR_SIZE - SCROLLBAR_MARGIN - paddingRight,
        SCROLLBAR_MARGIN,
        SCROLLBAR_SIZE,
        vTrackHeight,
      ),
      thumb: new Rect(
        canvasWidth - SCROLLBAR_SIZE - SCROLLBAR_MARGIN - paddingRight,
        SCROLLBAR_MARGIN + thumbY,
        SCROLLBAR_SIZE,
        thumbHeight,
      ),
      trackHeight: vTrackHeight,
      thumbHeight: thumbHeight,
      totalBox: totalBox,
      viewRect: viewRect,
    };
  }

  return info;
}

export function drawScrollbars(context, renderContext) {
  const info = getScrollbarInfo(renderContext);
  const { interactive } = renderContext.providers;

  const drawThumb = (thumb, id) => {
    const isHover = interactive.hoverHandlerId === id;
    const isMoving = interactive.movingHandler?.id === id;

    // 只绘制 thumb，不绘制轨道
    const thumbColor = isMoving
      ? 'rgba(100, 100, 100, 1)'
      : isHover
        ? 'rgba(130, 130, 130, 1)'
        : 'rgba(160, 160, 160, 0.8)';
    context.fillStyle = thumbColor;
    context.beginPath();
    const thumbRadius = Math.min(thumb.w, thumb.h) / 2 - 1;
    context.roundRect(thumb.x + 1, thumb.y + 1, thumb.w - 2, thumb.h - 2, thumbRadius);
    context.fill();
  };

  if (info.h) {
    drawThumb(info.h.thumb, 'scrollbar-h');
  }
  if (info.v) {
    drawThumb(info.v.thumb, 'scrollbar-v');
  }
}

export function scrollbarMoveAction(hitTestContext) {
  const { type, point, screenDeltaX, screenDeltaY, targetHandler, provider, stageContext } =
    hitTestContext;
  const interactive = provider;
  const { scale: scaleProvider } = stageContext.providers;

  if (type === EventTypes.down && targetHandler?.type === 'scrollbar') {
    interactive.movingHandler = targetHandler;
    interactive.pointStart = point;
    interactive.cursor.set('grabbing');
    return true;
  }

  if (type === EventTypes.move) {
    if (interactive.movingHandler?.type === 'scrollbar') {
      const handler = interactive.movingHandler;

      if (handler.id === 'scrollbar-h') {
        const trackWidth = handler.trackWidth;
        const thumbWidth = handler.thumbWidth;
        const scrollableTrackWidth = trackWidth - thumbWidth;
        const totalBox = handler.totalBox;
        const viewRect = handler.viewRect;
        const contentScrollableWidth = Math.max(0, totalBox.w - viewRect.w);

        if (scrollableTrackWidth > 0 && contentScrollableWidth > 0) {
          const deltaX = screenDeltaX;
          const scrollRatio = contentScrollableWidth / scrollableTrackWidth;
          scaleProvider.translate(-deltaX * scrollRatio, 0);
        }
      }

      if (handler.id === 'scrollbar-v') {
        const trackHeight = handler.trackHeight;
        const thumbHeight = handler.thumbHeight;
        const scrollableTrackHeight = trackHeight - thumbHeight;
        const totalBox = handler.totalBox;
        const viewRect = handler.viewRect;
        const contentScrollableHeight = Math.max(0, totalBox.h - viewRect.h);

        if (scrollableTrackHeight > 0 && contentScrollableHeight > 0) {
          const deltaY = screenDeltaY;
          const scrollRatio = contentScrollableHeight / scrollableTrackHeight;
          scaleProvider.translate(0, -deltaY * scrollRatio);
        }
      }

      stageContext.api.markNeedRender();
      return true;
    }

    if (targetHandler?.type === 'scrollbar') {
      interactive.cursor.set('grab');
      interactive.hoverHandlerId = targetHandler.id;
      stageContext.api.markNeedRender();
      return true;
    }
  }

  return false;
}
